import { ResolveBlock } from '@bechtel/client-blocks';
import {
  AppUser,
  bankersRound,
  EllipsisPlacement,
  PersonaMessages,
  SuspenseLoader,
  TextWithEllipsis,
  useName,
} from '@bechtel/client-common';
import { GET_DASHBOARD_STATS_POST_CLAIM } from '@bechtel/client-gql';
import { WorkPackageContext } from '@bechtel/client-work-packages/src/detail';
import {
  ActionContent,
  ClaimAction,
  CommodityContext,
  MilestoneActionButtonStyled,
  ReclaimAction,
  ResolveBlockAction,
  UnclaimAction,
  WeldAssign,
  WeldAssignAction,
  WeldoutList,
} from '@bechtel/client-work-packages/src/detail/commodities';
import {
  belowBreakpoint,
  Box,
  Heading,
  Icon,
  IconSize,
  Text,
  Theme,
  useMediaQuery,
  useSideSheetState,
} from '@bechtel/gds';
import {
  CommodityMilestone,
  CommodityStatusCode,
  CrewMember,
  MaterialCommodity,
  MilestoneStatusCode,
  OwnershipLevel,
  PersonaType,
  Scalars,
  Weld,
} from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { addDays, isBefore } from 'date-fns';
import { every, filter, find, map } from 'lodash';
import { FC, Suspense, useContext } from 'react';
import { ThemeContext } from 'styled-components';
import { WorkListDetailButton } from '~/worklist-detail-button';
import { WorkListMenuType } from '../../worklist-detail-button/types';
import {
  AvatarRegularStyled,
  ContentStyled,
  CrewMemberStyled,
  LabelStyled,
  WorklistInfoStyled,
  WorklistItemStyled,
} from './styled';
export interface WorkListInfoProps {
  item: CommodityMilestone;
  commodity: MaterialCommodity | Weld;
  isOutOfPlan: Scalars['Boolean'];
  hasAssociatedWeld: Scalars['Boolean'] | null | undefined;
}

export const WorkListInfo: FC<WorkListInfoProps> = ({ item, commodity, isOutOfPlan, hasAssociatedWeld }) => {
  return (
    <WorklistInfoStyled data-testid="worklist-info">
      <Heading size="400">
        {item.name} - {bankersRound(item.hours, 0)} hours
      </Heading>
      <Text color="grey.600" size="300">
        {commodity?.type.name} : {commodity?.tag}
      </Text>
      <Text color="grey.600" size="300">
        {commodity?.workPackage?.tag}
      </Text>
      {commodity?.costCodeId && (
        <Text color="grey.600" size="300">
          {`${t`Cost Code`} : ${commodity?.costCodeId}`}
        </Text>
      )}
      {isOutOfPlan && (
        <Text color="red.700" size="300" weight="600">
          <Trans>Out of Plan</Trans>
        </Text>
      )}
      {hasAssociatedWeld && (
        <ContentStyled data-testid="weld-statuses">
          <WeldoutList weldStatuses={item.weldStatuses} justifyContent={'flex-start'} showTooltip={false}></WeldoutList>
        </ContentStyled>
      )}
    </WorklistInfoStyled>
  );
};

export interface WeeklyWorklistItemProps {
  item: CommodityMilestone;
  showOwners: Scalars['Boolean'];
  isOutOfPlan: boolean;
  user: AppUser;
  onWorkListMenuSelect: (workListMenuType: WorkListMenuType) => void;
  crewMember: CrewMember;
  onActionUpdating?: () => void;
  onActionCompleted?: () => void;
  disableAction?: boolean;
}
export const WeeklyWorklistItem: FC<WeeklyWorklistItemProps> = ({
  item,
  showOwners,
  isOutOfPlan,
  user,
  crewMember,
  onActionUpdating,
  onActionCompleted,
  onWorkListMenuSelect,
  disableAction,
}) => {
  const { getFullName } = useName();
  const { breakpoints } = useContext<Theme>(ThemeContext);
  const { visible, open, close } = useSideSheetState(false);
  const { visible: visibleAssignWeld, open: openAssignWeld, close: closeAssignWeld } = useSideSheetState(false);
  const isWideScreen = useMediaQuery(belowBreakpoint(breakpoints.widescreen));
  const hasAssociatedWeld = item.weldStatuses && item.weldStatuses.length > 0;
  const currentProjectWeek = find(user?.project?.projectConfiguration?.projectWeeks, (projectWeek) =>
    projectWeek?.isCurrent ? true : false
  );
  const hasAllApprovedWeld = every(
    item?.weldStatuses,
    (weld) => weld?.status?.status === CommodityStatusCode.VT_APPROVED
  );

  const isMaterialCommodity = item.commodity.materialCommodity ? true : false;
  //@ts-ignore ignore undefined tupe warning as commodity wrapper will either have a weld or material commodity always
  const commodity: MaterialCommodity | Weld = isMaterialCommodity
    ? item.commodity.materialCommodity
    : item.commodity.weld;
  const assignedCrewMembers = [
    ...(commodity.workPackage?.primaryOwners ?? []),
    ...(commodity.workPackage?.secondaryOwners ?? []),
    ...(commodity.workPackage?.otherOwners ?? []),
  ];
  const commodityAssignee =
    !isMaterialCommodity &&
    find(commodity.commodityAssignees, (item) => {
      return item.personaType == PersonaType.FOREMAN;
    });
  const assignedWeldForeman = commodityAssignee ? commodityAssignee.assignee : undefined;

  const crewMembers = filter(
    commodity?.workPackage?.primaryOwners,
    (owner) => owner.persona === PersonaType.FOREMAN || owner.persona === PersonaType.GENERAL_FOREMAN
  );

  const claimRefetchQueries = [
    {
      query: GET_DASHBOARD_STATS_POST_CLAIM,
      variables: {
        input: {
          crewMemberId: crewMember?.id,
          personaType: crewMember?.persona,
          ownershipLevel: OwnershipLevel.PRIMARY,
          startDate: currentProjectWeek?.startDate,
          endDate: currentProjectWeek?.endDate,
        },
      },
    },
  ];

  return (
    <WorklistItemStyled data-testid="worklist-item">
      <WorkListInfo
        item={item}
        commodity={commodity}
        hasAssociatedWeld={hasAssociatedWeld}
        isOutOfPlan={isOutOfPlan}
        data-testid="worklist-info"
      />
      {showOwners && (
        <CrewMemberStyled data-testid="worklist-owners">
          {map(crewMembers, ({ crewMember, persona }) => {
            return (
              <Box key={`${item.id}-${persona}`} alignItems={{ sm: 'center' }}>
                <AvatarRegularStyled src={crewMember?.imageUrl ? crewMember?.imageUrl : undefined} />
                <Box flexDirection={{ sm: 'column' }} style={{ minWidth: '0' }}>
                  <Box mb={{ sm: '300' }}>
                    <TextWithEllipsis
                      size={isWideScreen ? '200' : '300'}
                      color="grey.1000"
                      weight="600"
                      ellipsisPlacement={EllipsisPlacement.END}
                    >
                      {crewMember?.firstName && getFullName(crewMember?.firstName, crewMember?.lastName)}
                    </TextWithEllipsis>
                  </Box>
                  <Text size="200" color="grey.600">
                    <Trans id={PersonaMessages[persona].id}></Trans>
                    {crewMember?.crew?.crewNumber && ` - ${crewMember?.crew?.crewNumber}`}
                  </Text>
                </Box>
              </Box>
            );
          })}
        </CrewMemberStyled>
      )}
      <Box
        alignSelf={{ sm: 'center' }}
        flexDirection={{ sm: 'column' }}
        flexShrink={{ sm: 0 }}
        data-testid="worklist-actions"
        style={{ width: showOwners ? '35%' : '52%' }}
      >
        <Box mt={{ sm: '400' }}>
          <WorkListDetailButton milestone={item} onWorkListMenuSelect={onWorkListMenuSelect} />
          <Box style={{ width: '50%' }}>
            <WorkPackageContext
              id={commodity.workPackage?.id}
              tag={commodity.workPackage?.tag}
              assignedCrewMembers={assignedCrewMembers}
              facilities={commodity.workPackage?.facilities}
            >
              <CommodityContext
                id={commodity.id}
                tag={commodity.tag}
                typeId={commodity.type.id}
                typeName={commodity.type.name}
              >
                {(!hasAssociatedWeld || hasAllApprovedWeld) && (
                  <>
                    {isMaterialCommodity && (
                      <>
                        {(item.currentStatus?.codeI18n === MilestoneStatusCode.PLANNED ||
                          item.currentStatus?.codeI18n === MilestoneStatusCode.BLOCKED) &&
                        item.blocks?.length ? (
                          <ResolveBlockAction
                            milestone={item}
                            status={item.currentStatus?.codeI18n}
                            onActionCompleted={onActionCompleted}
                            openBlockSidesheet={open}
                            disabled={disableAction}
                          ></ResolveBlockAction>
                        ) : (
                          item.currentStatus?.codeI18n === MilestoneStatusCode.PLANNED && (
                            <ClaimAction
                              milestone={item}
                              status={item.currentStatus?.codeI18n}
                              refetchQueries={claimRefetchQueries}
                              onActionUpdating={onActionUpdating}
                              onActionCompleted={onActionCompleted}
                              disabled={disableAction}
                            ></ClaimAction>
                          )
                        )}
                        {item.currentStatus?.codeI18n === MilestoneStatusCode.CLAIMED && (
                          <UnclaimAction
                            milestone={item}
                            status={item.currentStatus?.codeI18n}
                            refetchQueries={claimRefetchQueries}
                            disabled={
                              disableAction ||
                              isBefore(
                                new Date(item.currentStatus?.createdAt),
                                new Date(addDays(new Date(), -2).toISOString())
                              )
                            }
                            onActionUpdating={onActionUpdating}
                            onActionCompleted={onActionCompleted}
                          ></UnclaimAction>
                        )}
                        {item.currentStatus?.codeI18n === MilestoneStatusCode.REJECTED && (
                          <ReclaimAction
                            milestone={item}
                            status={item.currentStatus?.codeI18n}
                            refetchQueries={claimRefetchQueries}
                            onActionCompleted={onActionCompleted}
                            disabled={disableAction}
                          ></ReclaimAction>
                        )}
                      </>
                    )}
                    {item.currentStatus?.codeI18n === MilestoneStatusCode.APPROVED && (
                      <LabelStyled status={MilestoneStatusCode.APPROVED}>
                        <Box mr={{ sm: '300' }}>
                          <Icon icon="check" size={IconSize.MEDIUM} fillColor="blue.800" />
                        </Box>
                        <Text size={'300'} color={'grey.1000'}>
                          <Trans>Approved</Trans>
                        </Text>
                      </LabelStyled>
                    )}
                  </>
                )}
                {isMaterialCommodity &&
                  hasAssociatedWeld &&
                  !hasAllApprovedWeld &&
                  (item.blocks?.length ? (
                    <ResolveBlockAction
                      milestone={item}
                      status={item.currentStatus?.codeI18n}
                      refetchQueries={claimRefetchQueries}
                      onActionCompleted={onActionCompleted}
                      openBlockSidesheet={open}
                    ></ResolveBlockAction>
                  ) : (
                    <WeldAssignAction
                      milestone={item}
                      status={item.currentStatus?.codeI18n}
                      actionText={t`Assign`}
                      disabled={disableAction}
                    ></WeldAssignAction>
                  ))}
                {!isMaterialCommodity &&
                  (item.currentStatus?.codeI18n === MilestoneStatusCode.PLANNED ||
                    item.currentStatus?.codeI18n === MilestoneStatusCode.REJECTED ||
                    item.currentStatus?.codeI18n === MilestoneStatusCode.BLOCKED) && (
                    <MilestoneActionButtonStyled
                      status={item.currentStatus?.codeI18n}
                      onClick={openAssignWeld}
                      disabled={disableAction}
                    >
                      <ActionContent label={t`Assign`} icon="user-plus"></ActionContent>
                    </MilestoneActionButtonStyled>
                  )}
                <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
                  {visible && (
                    <ResolveBlock
                      workPackageId={commodity.workPackage?.id}
                      workPackageTag={commodity.workPackage?.tag}
                      assignedCrewMembers={assignedCrewMembers}
                      weldCommodityAssignees={commodity?.commodityAssignees}
                      facilities={commodity.workPackage?.facilities}
                      commodityTag={commodity.tag}
                      commodityTypeName={commodity.type.name}
                      milestone={item}
                      onClose={close}
                    />
                  )}
                </Suspense>
                <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
                  {visibleAssignWeld && <WeldAssign close={closeAssignWeld} existingAssignment={assignedWeldForeman} />}
                </Suspense>
              </CommodityContext>
            </WorkPackageContext>
          </Box>
        </Box>
      </Box>
    </WorklistItemStyled>
  );
};
